import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import {
  eIntegrationStatus,
  eIntegrationType,
} from 'src/providers/IntegrationsProvider/Integrations.enums';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { canSeeWapiDisconnectedBanner } from 'src/providers/SubscriptionProvider/Subscription.utils';

/**
 * Tells whether the open workspace pays for WhatsApp API and never connected a
 * number: hydrated, on a plan that includes the channel, the WHATSAPP_API row
 * reported by the latest response, and that row at `NO_ACCOUNT`.
 *
 * Both the Alert Center and the Chat empty state invite the workspace to
 * connect, so the four terms live here once — two copies of the same fact can
 * drift and leave one surface inviting a connected workspace to connect again.
 *
 * @returns {boolean} True when the workspace has WhatsApp API and no account yet.
 */
export function useWapiNeverConnected(): boolean {
  const { subscription } = useSubscriptionContext();
  const { whatsappBusinessIntegration, channelsInLatestResponse, isHydrated } =
    useIntegrationsContext();

  // The latest response has to have REPORTED the channel, not merely left a row
  // behind. The WhatsApp API slice sits at NO_ACCOUNT before the fetch resolves,
  // and bff-web aggregates channels: when its WhatsApp API call fails it drops
  // that row and still answers 200 with the channels that did resolve, so the
  // provider hydrates normally and the slice keeps its default. Reading the
  // slice alone therefore cannot tell "never connected" from "we could not find
  // out", and a connected workspace would be invited to connect its number
  // during any upstream WAPI outage.
  //
  // `integrations` cannot carry that distinction either, because a partial
  // response merges over the previous rows on purpose — a refetch must not
  // flash a connected channel back to its default. So a row omitted this round
  // survives, and row presence would report "no account" from an answer the
  // server never gave.
  const latestResponseReportedWhatsappApi = channelsInLatestResponse.includes(
    eIntegrationType.WHATSAPP_API,
  );

  return (
    isHydrated &&
    canSeeWapiDisconnectedBanner(subscription) &&
    latestResponseReportedWhatsappApi &&
    whatsappBusinessIntegration.state === eIntegrationStatus.NO_ACCOUNT
  );
}
